/**
 * Crew CSV Parser Utilities
 *
 * Functions for parsing crew CSV files and generating templates.
 */

import Papa from 'papaparse';
import {
  CrewCSVRowInput,
  CrewRowValidationResult,
  validateCrewRows,
  CREW_CSV_COLUMNS,
  CREW_REQUIRED_COLUMNS,
} from '../../../utils/crewCsvValidation';
import { BulkUserInput } from '../../../api/users';
import { isValidCSVFile, isFileSizeValid } from '../../events/CSVUpload/csvParser';

export { isValidCSVFile, isFileSizeValid };

/**
 * Result of parsing a crew CSV file
 */
export interface CrewParseResult {
  success: boolean;
  rows: CrewCSVRowInput[];
  validationResults: CrewRowValidationResult[];
  errors: string[];
}

/**
 * Parse a crew CSV file and validate its contents
 */
export function parseCrewCSV(file: File): Promise<CrewParseResult> {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        const errors: string[] = [];

        if (results.errors.length > 0) {
          results.errors.forEach((err) => {
            errors.push(`Row ${err.row}: ${err.message}`);
          });
        }

        // Check for required columns
        const headers = results.meta.fields || [];
        const missingColumns = CREW_REQUIRED_COLUMNS.filter(
          (col) => !headers.includes(col)
        );

        if (missingColumns.length > 0) {
          errors.push(
            `Missing required columns: ${missingColumns.join(', ')}`
          );
          resolve({
            success: false,
            rows: [],
            validationResults: [],
            errors,
          });
          return;
        }

        // Normalize row data
        const rows = (results.data as Record<string, string>[]).map((row) => {
          const normalized: CrewCSVRowInput = {
            firstName: row.firstName?.trim() || '',
            lastName: row.lastName?.trim() || '',
            email: row.email?.trim() || '',
            phone: row.phone?.trim() || undefined,
            role: row.role?.trim() || undefined,
          };
          return normalized;
        });

        const validationResults = validateCrewRows(rows);

        resolve({
          success: errors.length === 0,
          rows,
          validationResults,
          errors,
        });
      },
      error: (error) => {
        resolve({
          success: false,
          rows: [],
          validationResults: [],
          errors: [`Failed to parse CSV: ${error.message}`],
        });
      },
    });
  });
}

/**
 * Convert validated rows to API format for submission
 */
export function prepareCrewForAPI(
  validationResults: CrewRowValidationResult[]
): BulkUserInput[] {
  return validationResults
    .filter((r) => r.isValid && r.parsedData !== null)
    .map((r) => {
      const d = r.parsedData!;
      return {
        email: d.email,
        firstName: d.firstName,
        lastName: d.lastName,
        phone: d.phone || null,
        role: d.role as 'CREW' | 'ADMIN' | 'OWNER',
      };
    });
}

/**
 * Generate and download a crew CSV template
 */
export function downloadCrewTemplate(): void {
  const headers = CREW_CSV_COLUMNS.join(',');
  const exampleRow1 = [
    'John',
    'Smith',
    'john.smith@example.com',
    '555-123-4567',
    'CREW',
  ].join(',');
  const exampleRow2 = [
    'Jane',
    'Doe',
    'jane.doe@example.com',
    '',
    'ADMIN',
  ].join(',');

  const csvContent = [headers, exampleRow1, exampleRow2].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'crew_template.csv';
  link.click();
  URL.revokeObjectURL(url);
}
